<template>
  <div class="coding-test-case-editor border rounded p-3 mb-2 bg-light">
    <p class="small text-muted mb-2">
      <strong>怎么出题：</strong>填「期望输出」（程序打印的结果，如 <code>Hello</code> 或 <code>15</code>），在「参考答案」填参考 Python 代码。
      系统<strong>自动宽松比对</strong>（忽略空格、<code>8</code> 与 <code>8.0</code> 等），无需选择匹配模式。
    </p>

    <div class="mb-2">
      <label class="form-label small mb-1">本题是否需要输入？</label>
      <div class="d-flex flex-wrap gap-3">
        <label class="form-check-label small">
          <input v-model="local.needInput" class="form-check-input me-1" type="radio" :value="false" />
          否，直接运行出结果（如 print 固定内容、简单计算）
        </label>
        <label class="form-check-label small">
          <input v-model="local.needInput" class="form-check-input me-1" type="radio" :value="true" />
          是，需要 input() 读入数据
        </label>
      </div>
    </div>

    <label class="form-label small mb-1">题目说明（学生可见）</label>
    <textarea
      v-model="local.ioSpec"
      class="form-control form-control-sm mb-2"
      rows="2"
      :placeholder="local.needInput ? '例如：输入两个整数，输出它们的和' : '例如：输出 1 到 5 的累加和'"
    ></textarea>

    <label class="form-label small mb-1">代码模板（可选）</label>
    <textarea
      v-model="local.template"
      class="form-control form-control-sm mb-2 font-monospace"
      rows="3"
      :placeholder="local.needInput ? '# a = int(input())' : '# print(1 + 2 + 3)'"
    ></textarea>

    <div class="border rounded p-2 mb-2 bg-white">
      <div class="small fw-semibold mb-1">写法参考（选填，不影响「输出正确即满分」）</div>
      <p class="small text-muted mb-2 mb-md-1">勾选后会在阅卷时提示学生是否用到对应写法；<strong>只要输出对就给满分</strong>。</p>
      <div class="d-flex flex-wrap gap-3 align-items-center">
        <label class="form-check-label small mb-0">
          <input v-model="local.bonusHints.useFor" class="form-check-input me-1" type="checkbox" />
          使用了 for 循环
        </label>
        <label class="form-check-label small mb-0">
          <input v-model="local.bonusHints.useFunction" class="form-check-input me-1" type="checkbox" />
          定义了函数
        </label>
        <div class="d-flex align-items-center gap-1">
          <label class="small mb-0 text-nowrap">指定函数名</label>
          <input
            v-model="local.bonusHints.functionName"
            class="form-control form-control-sm"
            style="max-width: 120px"
            placeholder="如 calc"
          />
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between mb-2">
      <span class="form-label small mb-0">测试用例</span>
      <div class="d-flex gap-1">
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addSampleCase">+ 样例</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="addHiddenCase">+ 判分用例</button>
      </div>
    </div>

    <div v-if="!local.testCases.length" class="small text-muted mb-2">
      无输入题：只填「期望输出」即可，输入栏可留空。建议至少 1 个公开样例。
    </div>

    <div v-for="(tc, idx) in local.testCases" :key="'tc-' + idx" class="border rounded p-2 mb-2 bg-white">
      <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span class="small fw-semibold">{{ tc.isSample ? '公开样例' : '判分用例' }}</span>
        <label class="form-check-label small mb-0 ms-2">
          <input v-model="tc.isSample" class="form-check-input me-1" type="checkbox" />
          学生可见
        </label>
        <button type="button" class="btn btn-sm btn-outline-danger ms-auto" @click="removeCase(idx)">删除</button>
      </div>
      <div class="row g-2">
        <div v-if="local.needInput" class="col-md-6">
          <label class="form-label small mb-0">输入（每行一组数据）</label>
          <textarea v-model="tc.input" class="form-control form-control-sm font-monospace" rows="3" placeholder="如：&#10;3&#10;5"></textarea>
        </div>
        <div :class="local.needInput ? 'col-md-6' : 'col-12'">
          <label class="form-label small mb-0">期望输出 <span class="text-danger">*</span></label>
          <textarea v-model="tc.expectedOutput" class="form-control form-control-sm font-monospace" rows="3" placeholder="程序应打印的内容，如：8"></textarea>
        </div>
      </div>
    </div>

    <div class="d-flex flex-wrap gap-2 align-items-center">
      <button type="button" class="btn btn-sm btn-outline-success" :disabled="validating" @click="emitValidate">
        {{ validating ? '校验中…' : '用参考解验证用例' }}
      </button>
      <span v-if="validateMessage" :class="validateOk ? 'text-success' : 'text-danger'" class="small">{{ validateMessage }}</span>
    </div>
  </div>
</template>

<script>
import { defaultCodingConfig, newTestCase } from '../utils/codingJudge'

export default {
  name: 'CodingTestCaseEditor',
  props: {
    modelValue: {
      type: Object,
      default: () => defaultCodingConfig()
    },
    referenceCode: {
      type: String,
      default: ''
    },
    validating: {
      type: Boolean,
      default: false
    },
    validateMessage: {
      type: String,
      default: ''
    },
    validateOk: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'validate'],
  computed: {
    local: {
      get() {
        const v = this.modelValue || defaultCodingConfig()
        if (!v.bonusHints) {
          v.bonusHints = { useFor: false, useFunction: false, functionName: '' }
        }
        return v
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    }
  },
  methods: {
    patchConfig(partial) {
      this.$emit('update:modelValue', { ...this.local, ...partial })
    },
    addSampleCase() {
      const cases = [...(this.local.testCases || [])]
      cases.push(newTestCase(cases.filter((c) => c.isSample).length, true, this.local.needInput))
      this.patchConfig({ testCases: cases })
    },
    addHiddenCase() {
      const cases = [...(this.local.testCases || [])]
      cases.push(newTestCase(cases.filter((c) => !c.isSample).length, false, this.local.needInput))
      this.patchConfig({ testCases: cases })
    },
    removeCase(index) {
      const cases = [...(this.local.testCases || [])]
      cases.splice(index, 1)
      this.patchConfig({ testCases: cases })
    },
    emitValidate() {
      this.$emit('validate')
    }
  }
}
</script>

<style scoped>
.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
}
</style>
